import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  TextField
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Webhook as WebhookIcon,
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { adminApi } from '../services/api.ts';
import FirmanteDetalle from './FirmanteDetalle.tsx';
import WebhookAudit from './WebhookAudit.tsx';

interface DocumentoDetalleCompletoProps {
  open: boolean;
  onClose: () => void;
  documentoId: number;
}

interface DocumentoCompleto {
  id: number;
  titulo: string;
  descripcion: string;
  categoria: string;
  estado: string;
  fecha_creacion: string;
  fecha_expiracion: string;
  archivo_nombre: string;
  archivo_url: string;
  user: {
    id: number;
    nombre: string;
    email: string;
  };
  firmantes: any[];
  webhook_audits: any[];
}

const DocumentoDetalleCompleto: React.FC<DocumentoDetalleCompletoProps> = ({
  open,
  onClose,
  documentoId
}) => {
  const [documento, setDocumento] = useState<DocumentoCompleto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelMotivo, setCancelMotivo] = useState('');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (open && documentoId) {
      loadDocumentoDetalles();
    }
  }, [open, documentoId]);

  const loadDocumentoDetalles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.getDocumentoDetallesCompletos(documentoId);
      setDocumento(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error cargando detalles del documento');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };


  const handleDownloadSigned = async (agreementId: string) => {
    try {
      const response = await adminApi.downloadDocumentoFirmado(agreementId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `documento-firmado-${agreementId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error descargando documento firmado:', err);
    }
  };

  const handleCancelAgreement = async () => {
    if (!documento) return;
    
    try {
      setCancelling(true);
      // Buscar el primer link de firma para cancelar
      const linkFirma = documento.firmantes.find(f => f.link_firma)?.link_firma;
      if (!linkFirma) {
        setError('No se encontró link de firma para cancelar');
        return;
      }
      
      const response = await adminApi.cancelarAcuerdo(linkFirma.token, cancelMotivo);
      
      if (response.data.success) {
        setShowCancelDialog(false);
        setCancelMotivo('');
        // Recargar detalles del documento
        loadDocumentoDetalles();
        alert('Acuerdo cancelado exitosamente');
      } else {
        setError(response.data.message || 'Error al cancelar acuerdo');
      }
    } catch (err: any) {
      setError('Error al cancelar acuerdo: ' + (err.response?.data?.message || err.message));
    } finally {
      setCancelling(false);
    }
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'FIRMADO':
        return 'success';
      case 'FIRMANDO':
        return 'warning';
      case 'PENDIENTE':
        return 'info';
      case 'CANCELADO':
        return 'error';
      default:
        return 'default';
    }
  };

  const getEstadoIcon = (estado: string) => {
    switch (estado) {
      case 'FIRMADO':
        return <CheckCircleIcon />;
      case 'CANCELADO':
        return <ErrorIcon />;
      default:
        return <WarningIcon />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFirmantesCompletados = () => {
    if (!documento) return 0;
    return documento.firmantes.filter(f => f.estado === 'FIRMADO').length;
  };

  const getFirmantesPendientes = () => {
    if (!documento) return 0;
    return documento.firmantes.filter(f => f.estado !== 'FIRMADO').length;
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Cargando detalles del documento...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Alert severity="error" action={
            <Button color="inherit" size="small" onClick={loadDocumentoDetalles}>
              Reintentar
            </Button>
          }>
            {error}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!documento) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Alert severity="warning">
            No se encontró el documento.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" component="div">
              {documento.titulo}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ID: {documento.id} | Categoría: {documento.categoria}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Información General */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Chip
                    icon={getEstadoIcon(documento.estado)}
                    label={documento.estado}
                    color={getEstadoColor(documento.estado) as any}
                    variant="outlined"
                  />
                </Box>
                
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <CalendarIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Fecha de Creación"
                      secondary={formatDate(documento.fecha_creacion)}
                    />
                  </ListItem>
                  
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <TimeIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Fecha de Expiración"
                      secondary={formatDate(documento.fecha_expiracion)}
                    />
                  </ListItem>
                  
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Creado por"
                      secondary={`${documento.user.nombre} (${documento.user.email})`}
                    />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Progreso de Firmas
                  </Typography>
                  <Box display="flex" gap={1} mb={1}>
                    <Chip
                      icon={<CheckCircleIcon />}
                      label={`Completadas: ${getFirmantesCompletados()}`}
                      color="success"
                      size="small"
                    />
                    <Chip
                      label={`Pendientes: ${getFirmantesPendientes()}`}
                      color="warning"
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {getFirmantesCompletados()} de {documento.firmantes.length} firmantes han completado
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Documentos
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {documento.firmantes.some(f => f.s3_path) && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => {
                          const firmanteConFirma = documento.firmantes.find(f => f.s3_path);
                          if (firmanteConFirma?.agreement_id) {
                            handleDownloadSigned(firmanteConFirma.agreement_id);
                          }
                        }}
                      >
                        Firmado
                      </Button>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {documento.descripcion && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Descripción
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {documento.descripcion}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Tabs para diferentes secciones */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab 
              icon={<PersonIcon />} 
              label={`Firmantes (${documento.firmantes.length})`} 
              iconPosition="start"
            />
            <Tab 
              icon={<WebhookIcon />} 
              label="Auditoría de Webhooks" 
              iconPosition="start"
            />
            <Tab 
              icon={<InfoIcon />} 
              label="Información Técnica" 
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Contenido de las tabs */}
        <Box sx={{ mt: 2 }}>
          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Firmantes del Documento
              </Typography>
              {documento.firmantes.map((firmante) => (
                <FirmanteDetalle
                  key={firmante.id}
                  firmante={firmante}
                  onDownloadDocument={(url, filename) => {
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = filename;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                />
              ))}
            </Box>
          )}

          {activeTab === 1 && (
            <WebhookAudit documentoId={documentoId} />
          )}

          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Información Técnica
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Información del Archivo
                    </Typography>
                    <List dense>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary="Nombre del archivo"
                          secondary={documento.archivo_nombre}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary="URL del archivo"
                          secondary={
                            <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                              {documento.archivo_url}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </List>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Estadísticas
                    </Typography>
                    <List dense>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary="Total de firmantes"
                          secondary={documento.firmantes.length}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary="Webhooks registrados"
                          secondary={documento.webhook_audits.length}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary="Estado general"
                          secondary={documento.estado}
                        />
                      </ListItem>
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        {documento && documento.estado !== 'CANCELADO' && documento.estado !== 'FIRMADO' && (
          <Button
            onClick={() => setShowCancelDialog(true)}
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
          >
            Cancelar Acuerdo
          </Button>
        )}
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>

      {/* Dialog de confirmación de cancelación */}
      <Dialog open={showCancelDialog} onClose={() => setShowCancelDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancelar Acuerdo</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            ¿Estás seguro de que deseas cancelar este acuerdo de firma?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Esta acción cancelará el proceso de firma para todos los firmantes y no se puede deshacer.
          </Typography>
          <TextField
            fullWidth
            label="Motivo de cancelación (opcional)"
            multiline
            rows={3}
            value={cancelMotivo}
            onChange={(e) => setCancelMotivo(e.target.value)}
            placeholder="Describe el motivo de la cancelación..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)}>
            No Cancelar
          </Button>
          <Button 
            onClick={handleCancelAgreement}
            color="error"
            variant="contained"
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={20} /> : <CancelIcon />}
          >
            {cancelling ? 'Cancelando...' : 'Sí, Cancelar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default DocumentoDetalleCompleto;
